import { Button, Drawer } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../Core/Icon';
import TotpForm from '../../Forms/TotpForm';

export interface TotpSetupDrawerProps {
  /** Called when the drawer requests to close (close icon, mask click, Escape, or after setup success/expiry). Parent should reset the login flow, keeping the email field filled. */
  onClose: () => void;
  /** Called when the forced setup completes successfully; parent finishes the login. */
  onSuccess: () => void;
  /** Called when the partial auth token backing this flow expires or is rejected as invalid. */
  onTemporaryTotpExpired: () => void;
  /** Whether the drawer is open. */
  open: boolean;
  /** Partial auth token from the `403 TOTP_SETUP_REQUIRED` response, required while `open` is true. */
  partialAuthToken?: string;
  /** Seconds left until the partial token expires, or `null` when not counting down. */
  remainingSeconds: number | null;
  /** Email of the user completing the mandatory setup, shown in the intro copy. */
  userEmail?: string;
}

/** Snapshot of the data needed to render `TotpForm`, kept stable while the drawer is closing. */
interface TotpSetupSnapshot {
  partialAuthToken: string;
  userEmail: string;
}

/**
 * Bottom drawer hosting the mandatory TOTP setup flow (`TotpForm` with `setupChannel="forcedLogin"`),
 * triggered by a `403 TOTP_SETUP_REQUIRED` login response. Replaces the previous inline
 * in-form rendering with a full-width bottom sheet, per the login page UI refactor.
 */
export const TotpSetupDrawer = ({
  onClose,
  onSuccess,
  onTemporaryTotpExpired,
  open,
  partialAuthToken,
  remainingSeconds,
  userEmail,
}: TotpSetupDrawerProps) => {
  const { t } = useTranslation();

  // The parent nulls `partialAuthToken`/`userEmail` as soon as it resets on close, but antd's
  // Drawer keeps rendering the current children during the close animation. Snapshotting keeps
  // `TotpForm`'s props stable through that animation so it does not refire its setup effect with
  // a missing token (which would otherwise call the wrong endpoint and surface a bogus error).
  const [snapshot, setSnapshot] = useState<TotpSetupSnapshot | null>(null);

  useEffect(() => {
    if (open && partialAuthToken && userEmail) {
      setSnapshot({ partialAuthToken, userEmail });
    }
  }, [open, partialAuthToken, userEmail]);

  return (
    <Drawer
      title={t('auth.totpForcedSetupTitle')}
      placement="bottom"
      size="90vh"
      open={open}
      onClose={onClose}
      closable={false}
      extra={
        <Button
          type="text"
          aria-label={t('common.close')}
          onClick={onClose}
          icon={<Icon name="XIcon" className="h-4 w-4" />}
        />
      }
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflowY: 'auto',
        },
      }}
    >
      {snapshot && (
        // key: force a fresh TotpForm instance per partial auth token (new forced-setup session),
        // while keeping the same instance mounted for the same token during the close animation.
        <div key={snapshot.partialAuthToken} className="m-auto flex w-full max-w-lg flex-col gap-3">
          <p className="text-gray-dark text-center text-sm">
            {t('auth.totpForcedSetupIntro', { email: snapshot.userEmail })}
          </p>
          <TotpForm
            setupChannel="forcedLogin"
            partialAuthToken={snapshot.partialAuthToken}
            isSetupOnly
            onClose={onClose}
            onSuccess={onSuccess}
            onTemporaryTotpExpired={onTemporaryTotpExpired}
          />
          {remainingSeconds !== null && (
            <p className="text-gray-dark text-center text-xs">
              {t('auth.totpTimeRemaining', {
                time: `${Math.floor(remainingSeconds / 60)}:${(remainingSeconds % 60)
                  .toString()
                  .padStart(2, '0')}`,
              })}
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default TotpSetupDrawer;
